import { useState, useCallback, useEffect, useMemo } from 'react';
import { RowConfig } from '@/shared/types/table';

interface UseTableEditOptions {
  externalEditingRowIds?: (string | number)[];
  onEditingRowIdsChange?: (editingRowIds: (string | number)[]) => void;
  onEditModeChange?: (rowId: string | number, isEditing: boolean) => void;
}

export const useTableEdit = <T = unknown>(options: UseTableEditOptions = {}) => {
  const { externalEditingRowIds, onEditingRowIdsChange, onEditModeChange } = options;
  const [internalEditingRowIds, setInternalEditingRowIds] = useState<Set<string | number>>(
    new Set(),
  );

  // Sincronizar estado interno con IDs externos si se proporcionan
  useEffect(() => {
    if (externalEditingRowIds !== undefined) {
      setInternalEditingRowIds(new Set(externalEditingRowIds));
    }
  }, [externalEditingRowIds]);

  // Usar IDs externos si están definidos, de lo contrario usar estado interno
  const editingRowIds = useMemo(() => {
    return externalEditingRowIds !== undefined
      ? new Set(externalEditingRowIds)
      : internalEditingRowIds;
  }, [externalEditingRowIds, internalEditingRowIds]);

  // Helper para notificar cambios al padre
  const notifyChange = useCallback(
    (newIds: Set<string | number>) => {
      if (onEditingRowIdsChange) {
        onEditingRowIdsChange(Array.from(newIds));
      }
    },
    [onEditingRowIdsChange],
  );

  const toggleRowEdit = useCallback(
    (rowId: string | number) => {
      const newSet = new Set(editingRowIds);
      const wasEditing = newSet.has(rowId);
      if (wasEditing) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }

      // Ejecutar callback de cambio de modo de edición
      if (onEditModeChange) {
        onEditModeChange(rowId, !wasEditing);
      }

      // Si hay control externo, notificar al padre
      if (externalEditingRowIds !== undefined) {
        notifyChange(newSet);
      } else {
        // Si no hay control externo, actualizar estado interno
        setInternalEditingRowIds(newSet);
      }
    },
    [editingRowIds, externalEditingRowIds, notifyChange, onEditModeChange],
  );

  const startEdit = useCallback(
    (rowId: string | number) => {
      const newSet = new Set(editingRowIds);
      const wasEditing = newSet.has(rowId);
      newSet.add(rowId);

      // Ejecutar callback solo si no estaba editando antes
      if (!wasEditing && onEditModeChange) {
        onEditModeChange(rowId, true);
      }

      if (externalEditingRowIds !== undefined) {
        notifyChange(newSet);
      } else {
        setInternalEditingRowIds(newSet);
      }
    },
    [editingRowIds, externalEditingRowIds, notifyChange, onEditModeChange],
  );

  const stopEdit = useCallback(
    (rowId: string | number) => {
      const newSet = new Set(editingRowIds);
      const wasEditing = newSet.has(rowId);
      newSet.delete(rowId);

      // Ejecutar callback solo si estaba editando antes
      if (wasEditing && onEditModeChange) {
        onEditModeChange(rowId, false);
      }

      if (externalEditingRowIds !== undefined) {
        notifyChange(newSet);
      } else {
        setInternalEditingRowIds(newSet);
      }
    },
    [editingRowIds, externalEditingRowIds, notifyChange, onEditModeChange],
  );

  const stopAllEdits = useCallback(() => {
    if (externalEditingRowIds !== undefined) {
      notifyChange(new Set());
    } else {
      setInternalEditingRowIds(new Set());
    }
  }, [externalEditingRowIds, notifyChange]);

  const isRowEditing = useCallback(
    (rowId: string | number) => editingRowIds.has(rowId),
    [editingRowIds],
  );

  const applyEditState = useCallback(
    (rows: RowConfig<T>[]): RowConfig<T>[] => {
      return rows.map((row) => ({
        ...row,
        _isEditing: editingRowIds.has(row.id),
      }));
    },
    [editingRowIds],
  );

  return {
    editingRowIds,
    toggleRowEdit,
    startEdit,
    stopEdit,
    stopAllEdits,
    isRowEditing,
    applyEditState,
  };
};
